import React, { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import Modal from "src/components/Modal";
import { FirebaseDataService } from "src/services/Firebase/data";
import { getUserData } from "src/redux/selectors/user";
import { useSelector } from "react-redux";
import { Ticket } from "src/types";
import { colors } from "src/styles";

type RecurringScheduleActionMode = "monitor" | "edit" | "deactivate";

type RecurringScheduleActionModalProps = {
  isOpen: boolean;
  mode: RecurringScheduleActionMode;
  scheduleId?: string;
  ticket: Ticket;
  onClose: () => void;
  onCompleted: () => void;
};

const frequencyOptions = [
  { value: "one_time", label: "One Time" },
  { value: "weekly", label: "Weekly" },
  { value: "biweekly", label: "Bi-Weekly" },
  { value: "monthly", label: "Monthly" },
  { value: "quarterly", label: "Quarterly (every 3 months)" },
  { value: "semiannual", label: "Semi-Annually (every 6 months)" },
  { value: "annual", label: "Annually" },
];

const readStringField = (source: Record<string, unknown>, keys: string[]) => {
  for (const key of keys) {
    const value = source[key];
    if (value == null) continue;
    const text = String(value).trim();
    if (text) return text;
  }
  return "";
};

const toDateInputValue = (value?: string) => {
  if (!value) return "";
  const raw = String(value);
  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) return raw;
  const parsed = new Date(raw);
  if (Number.isNaN(parsed.getTime())) return "";
  return parsed.toISOString().slice(0, 10);
};

const normalizeDetails = (input: unknown): Record<string, unknown> => {
  if (!input || typeof input !== "object") return {};

  const payload = input as Record<string, unknown>;
  const nestedData = payload.data;

  if (nestedData && typeof nestedData === "object") {
    const inner = nestedData as Record<string, unknown>;
    if (inner.data && typeof inner.data === "object") {
      return inner.data as Record<string, unknown>;
    }
    return inner;
  }

  return payload;
};

const RecurringScheduleActionModal = ({
  isOpen,
  mode,
  scheduleId,
  ticket,
  onClose,
  onCompleted,
}: RecurringScheduleActionModalProps) => {
  const { id: loggedInUserId, isManager } = useSelector(getUserData);

  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState("");
  const [details, setDetails] = useState<Record<string, unknown>>({});

  const [frequency, setFrequency] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [deactivateReason, setDeactivateReason] = useState("");

  const effectiveScheduleId = scheduleId || ticket.recurringScheduleId || "";

  useEffect(() => {
    if (!isOpen || !effectiveScheduleId) return;

    const loadDetails = async () => {
      setIsLoading(true);
      setError("");
      try {
        const response = await FirebaseDataService.getRecurringWorkOrderScheduleDetails(
          loggedInUserId,
          !!isManager,
          effectiveScheduleId,
        );
        const normalized = normalizeDetails(response);
        setDetails(normalized);

        const loadedFrequency = readStringField(normalized, ["frequency", "scheduleFrequency", "schedule_frequency"]);
        const loadedStartDate = toDateInputValue(readStringField(normalized, ["startDate", "start_date"]));
        const loadedEndDate = toDateInputValue(readStringField(normalized, ["endDate", "end_date"]));

        setFrequency(loadedFrequency || ticket.recurringFrequency || "");
        setStartDate(loadedStartDate);
        setEndDate(loadedEndDate);
      } catch (fetchError) {
        console.error("Failed to load recurring schedule details:", fetchError);
        setError("Unable to load recurring schedule details.");
      } finally {
        setIsLoading(false);
      }
    };

    void loadDetails();
  }, [isOpen, effectiveScheduleId, loggedInUserId, isManager, ticket.recurringFrequency]);

  useEffect(() => {
    if (!isOpen) {
      setError("");
      setDeactivateReason("");
    }
  }, [isOpen]);

  const detailsView = useMemo(
    () => ({
      scheduleId: effectiveScheduleId,
      frequency:
        readStringField(details, ["frequency", "scheduleFrequency", "schedule_frequency"]) ||
        ticket.recurringFrequency ||
        "-",
      status: readStringField(details, ["status", "statusDescription", "status_description"]) || ticket.status || "-",
      location:
        readStringField(details, ["locationName", "location_name", "locationLabel", "location_label"]) ||
        ticket.location ||
        "-",
      nextRun:
        readStringField(details, ["nextRunDate", "next_run_date", "nextRunAt", "next_run_at"]) ||
        ticket.nextRunDate ||
        "-",
      startDate: readStringField(details, ["startDate", "start_date"]) || "-",
      endDate: readStringField(details, ["endDate", "end_date"]) || "-",
      linkedWonum: readStringField(details, ["linkedWonum", "linked_wonum"]) || ticket.linkedWonum || ticket.wonum,
    }),
    [details, effectiveScheduleId, ticket],
  );

  const onApprove = async () => {
    if (!effectiveScheduleId) {
      setError("Recurring schedule id is missing.");
      return;
    }

    if (mode === "monitor") {
      onClose();
      return;
    }

    setIsSaving(true);
    setError("");

    try {
      if (mode === "edit") {
        const currentFrequency = readStringField(details, ["frequency", "scheduleFrequency", "schedule_frequency"]);
        const currentStartDate = toDateInputValue(readStringField(details, ["startDate", "start_date"]));
        const currentEndDate = toDateInputValue(readStringField(details, ["endDate", "end_date"]));

        const patch: Record<string, unknown> = {};

        if (frequency.trim() && frequency !== currentFrequency) {
          patch.frequency = frequency;
        }
        if (startDate !== currentStartDate) {
          patch.startDate = startDate;
        }
        if (endDate !== currentEndDate) {
          patch.endDate = endDate;
        }

        if (!Object.keys(patch).length) {
          onClose();
          return;
        }

        await FirebaseDataService.updateRecurringWorkOrderSchedule(
          loggedInUserId,
          !!isManager,
          effectiveScheduleId,
          patch,
        );
      }

      if (mode === "deactivate") {
        await FirebaseDataService.deactivateRecurringWorkOrderSchedule(
          loggedInUserId,
          !!isManager,
          effectiveScheduleId,
          deactivateReason.trim() || undefined,
        );
      }

      onCompleted();
      onClose();
    } catch (saveError) {
      console.error("Failed to perform recurring schedule action:", saveError);
      setError(mode === "edit" ? "Unable to update recurring schedule." : "Unable to deactivate recurring schedule.");
    } finally {
      setIsSaving(false);
    }
  };

  const approveButtonText = mode === "monitor" ? "Close" : mode === "edit" ? "Save Changes" : "Deactivate";
  const title =
    mode === "monitor"
      ? "Monitor Recurring Schedule"
      : mode === "edit"
        ? "Edit Recurring Schedule"
        : "Deactivate Recurring Schedule";

  return (
    <Modal
      isShowing={isOpen}
      hide={onClose}
      title={title}
      onCancelClick={onClose}
      onApproveClick={onApprove}
      approveButtonText={approveButtonText}
      cancelButtonText="Cancel"
      approveButtonStyle={mode === "deactivate" ? "decline" : "primary"}
      disableApproveButton={isLoading || isSaving || (mode === "edit" && !frequency.trim())}
      isLoadingApproveButton={isSaving}
      maxWidth="760px"
    >
      <Wrapper>
        {isLoading ? <InfoText>Loading schedule details...</InfoText> : null}
        {!isLoading && mode === "monitor" ? (
          <DetailsGrid>
            <DetailRow>
              <DetailLabel>Schedule ID</DetailLabel>
              <DetailValue>{detailsView.scheduleId || "-"}</DetailValue>
            </DetailRow>
            <DetailRow>
              <DetailLabel>Frequency</DetailLabel>
              <DetailValue>{detailsView.frequency}</DetailValue>
            </DetailRow>
            <DetailRow>
              <DetailLabel>Status</DetailLabel>
              <DetailValue>{detailsView.status}</DetailValue>
            </DetailRow>
            <DetailRow>
              <DetailLabel>Location</DetailLabel>
              <DetailValue>{detailsView.location}</DetailValue>
            </DetailRow>
            <DetailRow>
              <DetailLabel>Next Run</DetailLabel>
              <DetailValue>{detailsView.nextRun}</DetailValue>
            </DetailRow>
            <DetailRow>
              <DetailLabel>Start Date</DetailLabel>
              <DetailValue>{detailsView.startDate}</DetailValue>
            </DetailRow>
            <DetailRow>
              <DetailLabel>End Date</DetailLabel>
              <DetailValue>{detailsView.endDate}</DetailValue>
            </DetailRow>
            <DetailRow>
              <DetailLabel>Linked Work Order</DetailLabel>
              <DetailValue>{detailsView.linkedWonum || "-"}</DetailValue>
            </DetailRow>
          </DetailsGrid>
        ) : null}

        {!isLoading && mode === "edit" ? (
          <FormGrid>
            <FormField>
              <Label>Frequency</Label>
              <Select value={frequency} onChange={(e) => setFrequency(e.target.value)}>
                <option value="">Select frequency...</option>
                {frequencyOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Select>
            </FormField>

            <FormField>
              <Label>Start Date</Label>
              <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
            </FormField>

            <FormField>
              <Label>End Date</Label>
              <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
            </FormField>
          </FormGrid>
        ) : null}

        {!isLoading && mode === "deactivate" ? (
          <FormField>
            <InfoText>
              This will deactivate the recurring schedule and stop future auto-generated PM work orders for it.
            </InfoText>
            <Label>Reason (optional)</Label>
            <Textarea
              rows={4}
              value={deactivateReason}
              onChange={(e) => setDeactivateReason(e.target.value)}
              placeholder="Add context for this deactivation..."
            />
          </FormField>
        ) : null}

        {!!error ? <ErrorText>{error}</ErrorText> : null}
      </Wrapper>
    </Modal>
  );
};

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const DetailsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px 14px;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const DetailRow = styled.div`
  border: 1px solid ${colors.grey.light3};
  background: ${colors.grey.light6};
  padding: 10px 12px;
`;

const DetailLabel = styled.div`
  color: ${colors.grey.dark6};
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  margin-bottom: 4px;
`;

const DetailValue = styled.div`
  color: ${colors.black.dark1};
  font-size: 13px;
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const FormField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const Label = styled.label`
  color: ${colors.grey.dark};
  font-size: 12px;
  font-weight: 600;
`;

const Input = styled.input`
  width: 100%;
  border: 1px solid ${colors.grey.default};
  background: ${colors.white.default};
  padding: 10px 12px;
  font-size: 13px;
`;

const Select = styled.select`
  width: 100%;
  border: 1px solid ${colors.grey.default};
  background: ${colors.white.default};
  padding: 10px 12px;
  font-size: 13px;
`;

const Textarea = styled.textarea`
  width: 100%;
  border: 1px solid ${colors.grey.default};
  background: ${colors.white.default};
  padding: 10px 12px;
  font-size: 13px;
  resize: vertical;
`;

const InfoText = styled.p`
  margin: 0;
  color: ${colors.grey.dark3};
  font-size: 13px;
`;

const ErrorText = styled.p`
  margin: 0;
  color: ${colors.red.default};
  font-size: 13px;
`;

export default RecurringScheduleActionModal;
